"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  BarChart3,
  LogOut,
  MessageSquare,
  Mic,
  Settings,
  User,
  Users,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

import { ClientResponseError } from "pocketbase";
// import GalaxyIcon from "@/assets/galaxy.svg?react";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import { usePocketBase } from "@/context/pocketbase-context";
import { useTranslation } from "react-i18next";

// Main menu items
const items = [
  {
    title: "voicechatMenu",
    url: "/app/voicechat",
    icon: Mic, // Or custom svg GalaxyIcon
    isEnabled: true, // To control visibility
    size: "lg", // Custom size for GalaxyIcon
  },
  {
    title: "dashboardMenu",
    url: "/app/metrics",
    icon: BarChart3,
    isEnabled: true, // To control visibility
  },
  {
    title: "interactionsMenu",
    url: "/app/interactions",
    icon: MessageSquare,
    isEnabled: true, // To control visibility
  },
];

const userItems = [
  {
    title: "profileMenu",
    url: "/app/profile",
    icon: User,
    isEnabled: true, // To control visibility
  },
  {
    title: "preferencesMenu",
    url: "/app/preferences",
    icon: Settings,
    isEnabled: false, // To control visibility
  },
];

export function AppSidebar() {
  const { logout } = usePocketBase();
  const { t } = useTranslation(["navigation"]);
  const { user } = usePocketBase();
  const navigate = useNavigate();

  const handleLogout = () => {
    try {
      console.log("Logging out");
      logout();
      navigate("/login");
    } catch (error) {
      const clientError = error as ClientResponseError;
      toast.error(
        `${clientError.response.message} ${JSON.stringify(clientError.response.data)}`,
      );
    }
  };

  function MenuSeparator() {
    return (
      <div
        className={`
            opacity-0 opacity-100
            transition-opacity duration-300 delay-75
            mb-4
          `}
      >
        <Separator />
      </div>
    );
  }

  return (
    <Sidebar
      collapsible="icon"
      variant="floating"
      className={`
        z-50
        w-16 hover:w-64 
        transition-all duration-300 ease-in-out
        group/sidebar
      `}
    >
      <SidebarHeader className="p-2">
        <div className="flex items-center gap-2 min-h-8">
          <img
            src="/iriz-logo-small.png"
            alt="IrizAI Logo"
            className="h-8 w-8 rounded-md bg-primary text-primary-foreground shrink-0"
          />
          <span
            className={`
            text-xl text-primary
            font-semibold whitespace-nowrap overflow-hidden
          `}
          >
            IrizAI
          </span>
        </div>
      </SidebarHeader>

      <SidebarContent className="pt-8">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map(
                (item) =>
                  (item.isEnabled || item.isEnabled === undefined) && (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        className="text-base text-muted-foreground hover:text-sidebar-secondary-foreground"
                        size={item.size == "lg" ? "lg" : "default"}
                      >
                        <a
                          href={item.url}
                          className="flex items-center gap-3 w-full"
                        >
                          <item.icon className="scale-125" />
                          <span>{t(item.title)}</span>
                        </a>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ),
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-1">
        {/* Horizontal divider before user menu */}
        <MenuSeparator />
        <SidebarMenu>
          <div className="mb-6">
            <SidebarMenuItem>
              <div className="pb-2">
                <SidebarMenuButton
                  className={`
                  px-1
                  justify-start
                  group-hover/sidebar:justify-start
                `}
                >
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarImage
                      src="/placeholder.svg?height=24&width=24"
                      alt="User"
                    />
                    <AvatarFallback className="text-sm bg-primary text-primary-foreground">
                      {user?.first_name?.charAt(0).toUpperCase() ||
                        user?.email?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div
                    className={`
                  flex items-center justify-between flex-1 min-w-0
                `}
                  >
                    <div className="flex flex-col items-start min-w-0">
                      <span className="text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                        {user?.first_name} {user?.last_name}
                      </span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                        {user?.email}
                      </span>
                    </div>
                  </div>
                </SidebarMenuButton>
              </div>
            </SidebarMenuItem>
            {userItems.map(
              (item) =>
                (item.isEnabled || item.isEnabled === undefined) && (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      className="text-base text-muted-foreground hover:text-sidebar-secondary-foreground"
                    >
                      <a
                        href={item.url}
                        className="flex items-center gap-3 w-full"
                      >
                        <item.icon className="scale-125" />
                        <span>{t(item.title)}</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ),
            )}
          </div>
          <MenuSeparator />
          {/* Log out button */}
          <SidebarMenuItem key="logout">
            <SidebarMenuButton
              asChild
              className="
                      px-4
                      mb-4
                      justify-start
                      group-hover/sidebar:justify-start
                      text-base text-muted-foreground hover:text-sidebar-secondary-foreground
                    "
              onClick={handleLogout}
            >
              <a className="flex items-center gap-3 w-full">
                <LogOut className="scale-125" />
                <span
                  className={`
                        text-sm
                        whitespace-nowrap overflow-hidden
                      `}
                >
                  {t("logoutMenu")}
                </span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
